// CheckCompany.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import "./css/checkcompany.css";
import "./components/css/questionnareform.css";
import {
  validateCompany,
  syncCompany,
  ensureWorkspace,
  getCompanyValuation,
  getCompanyFinancials,
  request,
  API_BASE,
  getCsrfToken,
} from "../services/server";
import { useAuth } from "../services/AuthContext";
import NoAccess from "./components/NoAccess";
import OwnerQuestionnaireForm from "./components/OwnerQuestionnaireForm";
import RiskProfileQuestionnaireForm from "./components/RiskProfileQuestionnaireForm";
import { Link } from "react-router-dom";

const REQUIRED_UPLOADS = 5;
const MIN_FOR_PRELIM = 1;
const ORG_DEBOUNCE_MS = 500;

function onlyDigits(value) {
  return (value || "").replace(/\D/g, "");
}

// Dev shortcut — type "1" through "25" into the orgnr input (as a
// complete value, not a prefix) and it expands on blur to the
// zero-padded seeded orgnr 0000000001 … 0000000025. Real Swedish
// orgnrs never start with three zeros, so this range is safe to
// reserve for test companies without collision risk.
function resolveFakeAlias(raw) {
  const s = String(raw || "").trim();
  if (!/^\d{1,2}$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n) || n < 1 || n > 25) return null;
  return String(n).padStart(10, "0");
}

function normalizeWebsite(value) {
  const u = (value || "").trim();
  if (!u) return { normalized: "", ok: true, msg: "" };

  const withProto = /^https?:\/\//i.test(u) ? u : `https://${u}`;
  try {
    const url = new URL(withProto);
    return { normalized: url.toString(), ok: true, msg: "OK (format)." };
  } catch {
    return { normalized: u, ok: false, msg: "Ogiltig webbadress (format)." };
  }
}

function renameWithPrefix(file, prefix, year) {
  try {
    const desiredStart = `${prefix}_`;
    if (file.name.toLowerCase().startsWith(desiredStart)) return file;
    const safeYear = year || "okand";
    const newName = `${prefix}_${safeYear}_${file.name}`;
    return new File([file], newName, { type: file.type });
  } catch {
    return file;
  }
}

function createAnnualSlots() {
  const y = new Date().getFullYear();
  return Array.from({ length: REQUIRED_UPLOADS }, (_, i) => ({
    year: y - 1 - i,
    fromBv: false,
    file: null,
  }));
}

function emptyValuation() {
  return {
    loading: false,
    equityLow: null,
    equityHigh: null,
    evLow: null,
    evHigh: null,
    asOfDate: null,
    metricLabel: "",
  };
}

function createKoncernCompany() {
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    orgnr: "",
    website: "",
    orgStatus: { loading: false, ok: false, msg: "" },
    webStatus: { ok: true, msg: "" },
    annualSlots: createAnnualSlots(),
    sieFile: null,
    syncStatus: { loading: false, msg: "", done: false },
    hasAnnualReports: null,
    annualReportsCount: 0,
    bvReportsRaw: [],
    valuation: emptyValuation(),
  };
}

function formatRange(low, high) {
  if (low == null || high == null) return "Saknas";
  return `${low.toLocaleString("sv-SE")} – ${high.toLocaleString("sv-SE")} SEK`;
}

function mapMetricLabel(metric) {
  if (!metric) return "";
  switch (metric) {
    case "result_after_financial_items":
      return "Resultat efter finansiella poster";
    default:
      return metric.replace(/_/g, " ");
  }
}

function nameFromStatusMsg(msg) {
  if (!msg || !msg.includes(" · ")) return null;
  return msg.split(" · ").slice(1).join(" · ") || null;
}

function extractValidationName(data, fallback) {
  return (
    data?.organisation?.name ||
    data?.organisation?.organisationer?.[0]?.organisationsnamn?.organisationsnamnLista?.[0]?.namn ||
    data?.organisation?.organisationsnamn?.organisationsnamnLista?.[0]?.namn ||
    data?.organisationer?.[0]?.organisationsnamn?.organisationsnamnLista?.[0]?.namn ||
    fallback
  );
}

function extractReportYears(reports) {
  return (Array.isArray(reports) ? reports : [])
    .map((r) => {
      const y =
        r.year ??
        r.bokslutYear ??
        r.verksamhetsar ??
        (r.bokslutDatum && String(r.bokslutDatum).slice(0, 4)) ??
        (r.balance_date && String(r.balance_date).slice(0, 4));
      return typeof y === "string" || typeof y === "number"
        ? parseInt(String(y), 10)
        : null;
    })
    .filter(Boolean);
}

function CompactSummary({ label, orgnr, orgStatusMsg, annualSlots, sieFile, website, onClick }) {
  const name = orgStatusMsg && orgStatusMsg.includes(" · ")
    ? orgStatusMsg.split(" · ").slice(1).join(" · ")
    : orgnr || label;
  const filledCount = annualSlots.filter((s) => s.fromBv || !!s.file).length;
  const domain = website ? website.replace(/^https?:\/\//, "").split("/")[0] : null;

  return (
    <button type="button" className="checkcompany-compact" onClick={onClick}>
      <div className="checkcompany-compact-info">
        <span className="checkcompany-compact-name">{name}</span>
        {orgnr && name !== orgnr && <span className="checkcompany-compact-orgnr">{orgnr}</span>}
        {label && <span className="checkcompany-compact-badge">{label}</span>}
      </div>
      <div className="checkcompany-compact-meta">
        <div className="checkcompany-compact-dots">
          {annualSlots.map((s, i) => {
            const st = s.status || (s.fromBv ? "parsed" : "empty");
            const isParsed = st === "parsed" || !!s.file;
            const isPending = st === "fetched" && !s.file;
            return (
              <span
                key={i}
                className={`checkcompany-compact-dot${isParsed ? " checkcompany-compact-dot--on" : ""}`}
                style={isPending ? { background: "#f59e0b" } : undefined}
              />
            );
          })}
          <span className="checkcompany-compact-dots-label">{filledCount}/{annualSlots.length}</span>
        </div>
        {sieFile && <span className="checkcompany-compact-tag">SIE ✓</span>}
        {domain && <span className="checkcompany-compact-tag checkcompany-compact-tag--web">{domain}</span>}
      </div>
      <span className="checkcompany-compact-chevron">›</span>
    </button>
  );
}

function CompanyAccordion({
  company,
  index,
  active,
  onToggle,
  onChangeField,
  onOrgBlur,
  onWebsiteBlur,
  onBokslutPick,
  onSiePick,
  onDropAnnual,
  onDropSie,
  onRemove,
  canRemove,
  isLast,
  onAddNext,
  fileInputRefs,
  sieInputRefs,
}) {
  const availableAnnualCount = company.annualSlots.filter((s) => s.fromBv || !!s.file).length;
  const companyName = company.orgStatus.ok && company.orgStatus.msg?.includes(" · ")
    ? company.orgStatus.msg.split(" · ").slice(1).join(" · ")
    : null;

  return (
    <div className="checkcompany-koncern-accordion" style={{ position: "relative" }}>
      {/* X button to remove this company */}
      {canRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 2,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 16,
            lineHeight: 1,
            padding: "2px 6px",
            borderRadius: 4,
          }}
          title="Ta bort"
          onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
        >
          ✕
        </button>
      )}
      <button
        type="button"
        className="checkcompany-koncern-accordion-header"
        onClick={onToggle}
      >
        {/* Compact dots row shown when collapsed */}
        {!active ? (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: 14 }}>
                {companyName || `Dotterbolag ${index + 1}`}
                {!companyName && company.orgnr ? ` · ${company.orgnr}` : ""}
              </strong>
              {companyName && <div style={{ fontSize: 12, color: "#6b7280" }}>{company.orgnr}</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {company.annualSlots.map((s, i) => {
                const st = s.status || (s.fromBv ? "parsed" : "empty");
                const isParsed = st === "parsed" || !!s.file;
                const isPending = st === "fetched" && !s.file;
                return (
                  <span
                    key={i}
                    className={`checkcompany-compact-dot${isParsed ? " checkcompany-compact-dot--on" : ""}`}
                    style={isPending ? { background: "#f59e0b" } : undefined}
                  />
                );
              })}
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 2 }}>
                {availableAnnualCount}/{company.annualSlots.length}
              </span>
            </div>
            {company.sieFile && <span className="checkcompany-compact-tag" style={{ flexShrink: 0 }}>SIE ✓</span>}
            <span style={{ color: "#9ca3af", fontSize: 18, flexShrink: 0, marginRight: 18 }}>+</span>
          </>
        ) : (
          <>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 14 }}>
                {companyName || `Dotterbolag ${index + 1}`}
              </strong>
            </div>
            <span style={{ color: "#9ca3af", fontSize: 18, flexShrink: 0, marginRight: 18 }}>−</span>
          </>
        )}
      </button>

      {active && (
        <div className="checkcompany-koncern-accordion-body">
          <div className="checkcompany-block">
            <label className="checkcompany-label">Organisationsnummer</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder="t.ex. 5562820745"
              value={company.orgnr}
              onChange={(e) => onChangeField(index, "orgnr", onlyDigits(e.target.value))}
              onBlur={() => onOrgBlur(index)}
              className="checkcompany-input"
            />
            <small className={`checkcompany-small ${company.orgStatus.ok ? "checkcompany-ok" : ""}`}>
              {company.orgStatus.loading
                ? "Validerar org.nr…"
                : company.orgStatus.msg || "Vi validerar data."}
            </small>
          </div>

          <div className="checkcompany-block">
            <label className="checkcompany-label">Webbplats (valfritt)</label>
            <input
              type="url"
              placeholder="https://example.com"
              value={company.website}
              onChange={(e) => onChangeField(index, "website", e.target.value)}
              onBlur={() => onWebsiteBlur(index)}
              className="checkcompany-input"
            />
            <small className={`checkcompany-small ${company.website && company.webStatus.ok ? "checkcompany-ok" : ""}`}>
              {!company.website
                ? "Valfritt. Vi kontrollerar formatet när du lämnar fältet."
                : company.webStatus.ok
                  ? company.webStatus.msg || "OK (format)."
                  : company.webStatus.msg || "Ogiltig webbadress (format). Du kan ändå fortsätta."}
            </small>
          </div>

          <div className="checkcompany-block">
            <h3 className="checkcompany-subheading">Bokslut – senaste 5 räkenskapsår</h3>
            <p className="checkcompany-small">
              {availableAnnualCount >= 3
                ? "Vi har minst 3 bokslut – det ger ett bättre underlag för värderingen."
                : availableAnnualCount === 2
                  ? "Vi har 2 bokslut – det räcker för en preliminär värdering."
                  : availableAnnualCount === 1
                    ? "Vi har 1 bokslut – det räcker för en första indikation."
                    : "Koppla bokslut genom att släppa filer i rutorna nedan eller hämta automatiskt."}
            </p>

            <div className="checkcompany-annual-grid">
              {company.annualSlots.map((slot, slotIndex) => {
                const filled = slot.fromBv || !!slot.file;
                return (
                  <div
                    key={slot.year}
                    className={`checkcompany-slot ${filled ? "checkcompany-slot--filled" : ""}`}
                    onClick={() => {
                      if (!filled) fileInputRefs.current[company.id]?.[slotIndex]?.click();
                    }}
                    onDragOver={(e) => {
                      if (filled) return;
                      e.preventDefault();
                      e.currentTarget.classList.add("checkcompany-slot-dragover");
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove("checkcompany-slot-dragover");
                    }}
                    onDrop={(e) => {
                      if (filled) return;
                      e.preventDefault();
                      e.currentTarget.classList.remove("checkcompany-slot-dragover");
                      onDropAnnual(index, slotIndex, e);
                    }}
                  >
                    <div className="checkcompany-slot-header">
                      <span className="checkcompany-slot-title">Bokslut {slot.year}</span>
                      {slot.fromBv && <span className="checkcompany-slot-tag">Hämtat</span>}
                    </div>

                    {!filled && (
                      <div className="checkcompany-slot-body">
                        <span className="checkcompany-slot-instruction">Släpp fil här eller klicka</span>
                      </div>
                    )}

                    {filled && (
                      <div className="checkcompany-slot-check">
                        <span>✓</span>
                      </div>
                    )}

                    <input
                      ref={(el) => {
                        if (!fileInputRefs.current[company.id]) fileInputRefs.current[company.id] = [];
                        fileInputRefs.current[company.id][slotIndex] = el;
                      }}
                      type="file"
                      accept=".pdf,.zip,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      onChange={(e) => onBokslutPick(index, slotIndex, e)}
                    />
                  </div>
                );
              })}

              <div
                className={`checkcompany-slot checkcompany-slot--sie ${company.sieFile ? "checkcompany-slot--filled" : ""}`}
                onClick={() => {
                  if (!company.sieFile) sieInputRefs.current[company.id]?.click();
                }}
                onDragOver={(e) => {
                  if (company.sieFile) return;
                  e.preventDefault();
                  e.currentTarget.classList.add("checkcompany-slot-dragover");
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove("checkcompany-slot-dragover");
                }}
                onDrop={(e) => {
                  if (company.sieFile) return;
                  e.preventDefault();
                  e.currentTarget.classList.remove("checkcompany-slot-dragover");
                  onDropSie(index, e);
                }}
              >
                <div className="checkcompany-slot-header">
                  <span className="checkcompany-slot-title">SIE-fil (senaste 12 mån)</span>
                  <span className="checkcompany-slot-tag checkcompany-slot-tag--optional">Valfritt</span>
                </div>

                {!company.sieFile && (
                  <div className="checkcompany-slot-body">
                    <span className="checkcompany-slot-instruction">
                      Släpp SIE här eller klicka — annars efterfrågas filen från säljaren i säljarportalen.
                    </span>
                  </div>
                )}

                {company.sieFile && (
                  <div className="checkcompany-slot-check">
                    <span>✓</span>
                  </div>
                )}

                <input
                  ref={(el) => {
                    sieInputRefs.current[company.id] = el;
                  }}
                  type="file"
                  accept=".sie,.se,.si"
                  style={{ display: "none" }}
                  onChange={(e) => onSiePick(index, e)}
                />
              </div>
            </div>
          </div>

          <div className="checkcompany-footer" style={{ justifyContent: "space-between" }}>
            <span className="checkcompany-status">
              {company.orgStatus.ok ? "✓ Org.nr OK" : "• Org.nr saknas"} ·{" "}
              {company.website
                ? company.webStatus.ok
                  ? "✓ Webb (format OK)"
                  : "• Webb (format fel – men valfritt)"
                : "• Webb ej angiven"} ·{" "}
              {availableAnnualCount >= MIN_FOR_PRELIM ? "✓ Minst 1 bokslut" : "• Minst 1 bokslut krävs"} ·{" "}
              {company.sieFile ? "✓ SIE vald" : "• SIE valfri"}
            </span>

            {canRemove && (
              <button
                type="button"
                className="checkcompany-choice-btn checkcompany-choice-btn--ghost"
                onClick={() => onRemove(index)}
              >
                Ta bort
              </button>
            )}
          </div>

          {isLast && onAddNext && (
            <button type="button" className="checkcompany-add-company-btn" onClick={onAddNext}>
              + Lägg till ytterligare dotterbolag
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CheckCompany() {
  const { user } = useAuth();
  const isBroker = Boolean(user?.global_nav?.is_broker);

  if (user && !isBroker) {
    return (
      <Main>
        <NoAccess message="Snabbkollen är endast tillgänglig för mäklare." />
      </Main>
    );
  }

  return <CheckCompanyInner />;
}

// Emails that unlock the "fake N" orgnr alias (see resolveFakeAlias).
// Keep the list small and explicit — this is a dev shortcut, not a
// production feature.
const _FAKE_ALIAS_EMAILS = new Set(["david@qada.io"]);

function CheckCompanyInner() {
  const [step, setStep] = useState("check");
  const { user: _authUser } = useAuth();
  const fakeAliasEnabled = !!(
    _authUser?.email &&
    _FAKE_ALIAS_EMAILS.has(String(_authUser.email).trim().toLowerCase())
  );

  const initialOrgnr = (() => {
    if (typeof window === "undefined") return "";
    try {
      const sp = new URLSearchParams(window.location.search);
      return onlyDigits(sp.get("orgnr") || "");
    } catch {
      return "";
    }
  })();

  const [orgnr, setOrgnr] = useState(initialOrgnr);
  const [website, setWebsite] = useState("");

  const [orgStatus, setOrgStatus] = useState({ loading: false, ok: false, msg: "" });
  const [webStatus, setWebStatus] = useState({ ok: true, msg: "" });

  const [hasAnnualReports, setHasAnnualReports] = useState(null);
  const [annualReportsCount, setAnnualReportsCount] = useState(0);
  const [bvReportsRaw, setBvReportsRaw] = useState([]);

  const [syncStatus, setSyncStatus] = useState({ loading: false, msg: "", done: false });

  const [fetchingFinancials, setFetchingFinancials] = useState(false);
  // True while the backend is OCR'ing scanned annual-report PDFs in the
  // background — drives the per-slot spinner on pending years.
  const [ocrInProgress, setOcrInProgress] = useState(false);
  const ocrPollTimerRef = useRef(null);
  const [financialsFetched, setFinancialsFetched] = useState(false);
  const [upstreamError, setUpstreamError] = useState(false);

  const [isKoncern, setIsKoncern] = useState(false);
  const [partOfGroup, setPartOfGroup] = useState(false);
  const [groupSuggestions, setGroupSuggestions] = useState([]); // [{orgnr, name, checked}]
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [mainCompacted, setMainCompacted] = useState(false);
  const [koncernCompanies, setKoncernCompanies] = useState([]);
  const [activeKoncernIndex, setActiveKoncernIndex] = useState(null);
  const [koncernAdding, setKoncernAdding] = useState(false);
  const [koncernDone, setKoncernDone] = useState([]);
  const [koncernError, setKoncernError] = useState("");
  const [openValuationId, setOpenValuationId] = useState("main");

  const initialYears = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: REQUIRED_UPLOADS }, (_, i) => y - 1 - i);
  }, []);

  const [yearsAvailable, setYearsAvailable] = useState(null); // null = not fetched yet
  const [annualSlots, setAnnualSlots] = useState(
    initialYears.map((year) => ({ year, fromBv: false, file: null }))
  );

  const [sieFile, setSieFile] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [processMsg, setProcessMsg] = useState("Processing…");

  const orgTimerRef = useRef(null);
  const koncernOrgTimersRef = useRef({});
  const fileInputRefs = useRef([]);
  const sieInputRef = useRef(null);
  const koncernFileInputRefs = useRef({});
  const koncernSieInputRefs = useRef({});

  const [valuation, setValuation] = useState(emptyValuation());

  const availableAnnualCount = useMemo(
    () => annualSlots.filter((s) => s.fromBv || !!s.file).length,
    [annualSlots]
  );
  const pendingOcrCount = useMemo(
    () => annualSlots.filter((s) => s.status === "fetched" && !s.file).length,
    [annualSlots]
  );

  // ── "Hämta" button handler — fetches financials + group from BolagsAPI ──
  const [fetchError, setFetchError] = useState("");
  const [fetchHint, setFetchHint] = useState("");

  // Clean up the OCR poll on unmount so we don't keep hitting the
  // endpoint from a stale component.
  useEffect(() => () => {
    if (ocrPollTimerRef.current) clearTimeout(ocrPollTimerRef.current);
  }, []);

  const handleFetchFinancials = useCallback(async () => {
    const q = onlyDigits(orgnr);
    if (!q || q.length !== 10) return;
    setFetchingFinancials(true);
    setFetchError("");
    setFetchHint("");
    setUpstreamError(false);
    const hintTimer = setTimeout(() => {
      setFetchHint("Boksluten skannas och tolkas — detta kan ta upp till en minut…");
    }, 5000);
    try {
      // Fetch financials. Koncern auto-fetch removed 2026-06-01 —
      // brokers now explicitly opt in to multi-company mode via the
      // "Flera bolag i koncernen ska säljas"-button below. Saves an
      // upstream API call per lookup and avoids surprising the broker
      // with a koncern picker for groups that aren't actually being
      // sold together.
      const finData = await getCompanyFinancials(q).catch(() => null);

      if (finData?.upstream_error) {
        // Surface the specific TIC error code + message instead of
        // the generic "Fel hos leverantör". Categorised by backend:
        //   not_found    — orgnr unknown to TIC
        //   plan_limited — Pro plan doesn't cover the endpoint
        //   rate_limit   — TIC rate limit (60/min) hit
        //   auth         — API key bad / missing
        //   upstream     — timeout / 5xx / other
        setUpstreamError({
          code: finData.upstream_error_code || "upstream",
          detail: finData.upstream_error_detail || "Fel hos leverantör.",
        });
      }

      // Update annual slots from response — years returned by API
      if (finData?.years?.length) {
        const returnedYears = finData.years.slice(0, 5);
        setYearsAvailable(returnedYears.length);
        setAnnualSlots(
          returnedYears.map((y) => {
            const end = y.fiscal_year_end || "";
            const yr = parseInt(end.slice(0, 4), 10);
            const st = y._status || (y._parse_method === "api" ? "parsed" : y._parse_method === "pending" ? "fetched" : y._parse_method === "empty" ? "empty" : "available");
            return {
              year: yr,
              fromBv: st === "parsed" || st === "fetched",
              status: st,
              file: null,
            };
          })
        );
      } else {
        setYearsAvailable(0);
      }

      // Backend signals when older scanned PDFs are still being OCR'd in
      // the background. Start a poll that refreshes the slot list as new
      // years arrive — gives the broker a "Claude is thinking" feel
      // instead of a misleading "data hämtad"-state with too few years.
      if (finData?.ocr_in_progress) {
        setOcrInProgress(true);
        let pollCount = 0;
        const poll = async () => {
          pollCount += 1;
          try {
            const fresh = await getCompanyFinancials(q).catch(() => null);
            if (fresh?.years?.length) {
              const slot = fresh.years.slice(0, 5).map((y) => {
                const end = y.fiscal_year_end || "";
                const yr = parseInt(end.slice(0, 4), 10);
                const st = y._status || (y._parse_method === "api" ? "parsed" : y._parse_method === "pending" ? "fetched" : y._parse_method === "empty" ? "empty" : "available");
                return { year: yr, fromBv: st === "parsed" || st === "fetched", status: st, file: null };
              });
              setYearsAvailable(slot.length);
              setAnnualSlots(slot);
            }
            // Stop when backend says it's done OR we've polled enough.
            // 8 polls × 12s = ~96s, generous for 2-3 PDFs through Azure DI + LLM.
            if (!fresh?.ocr_in_progress || pollCount >= 8) {
              setOcrInProgress(false);
              return;
            }
          } catch {
            // Transient network error — keep polling unless we've hit the cap.
            if (pollCount >= 8) {
              setOcrInProgress(false);
              return;
            }
          }
          ocrPollTimerRef.current = setTimeout(poll, 12000);
        };
        ocrPollTimerRef.current = setTimeout(poll, 12000);
      }

      // Koncern-based auto-suggestions removed — see comment above.
      // Brokers opt in via the "Flera bolag i koncernen ska säljas"
      // button; group members are added manually there.

      setFinancialsFetched(true);
      if (!finData?.years?.length) {
        setFetchError("Inga finansiella data hittades.");
      }
    } catch (e) {
      console.error("Hämta failed:", e);
      setFetchError(e?.body?.detail || e?.message || "Kunde inte hamta data.");
    } finally {
      clearTimeout(hintTimer);
      setFetchHint("");
      setFetchingFinancials(false);
    }
  }, [orgnr]);

  const canProceed =
    step === "check" &&
    orgStatus.ok &&
    (financialsFetched || availableAnnualCount >= MIN_FOR_PRELIM);

  const markSlotsFromBv = useCallback((reports) => {
    const yearSet = new Set(extractReportYears(reports));
    setAnnualSlots((prev) =>
      prev.map((slot) => ({
        ...slot,
        fromBv: yearSet.has(slot.year),
      }))
    );
  }, []);

  const markKoncernSlotsFromBv = useCallback((companyIndex, reports) => {
    const yearSet = new Set(extractReportYears(reports));
    setKoncernCompanies((prev) =>
      prev.map((company, idx) =>
        idx !== companyIndex
          ? company
          : {
              ...company,
              annualSlots: company.annualSlots.map((slot) => ({
                ...slot,
                fromBv: yearSet.has(slot.year),
              })),
            }
      )
    );
  }, []);

  const fetchMainValuation = useCallback(async (q) => {
    if (!q || q.length !== 10) {
      setValuation(emptyValuation());
      return;
    }

    setValuation((prev) => ({ ...prev, loading: true }));

    try {
      const data = await getCompanyValuation(q);
      if (!data) {
        setValuation(emptyValuation());
        return;
      }

      const equityLow =
        data.final_value_low ??
        data.equity_value_low ??
        data.ev_value_low ??
        data.base_value_low ??
        null;

      const equityHigh =
        data.final_value_high ??
        data.equity_value_high ??
        data.ev_value_high ??
        data.base_value_high ??
        null;

      const evLow = data.ev_value_low ?? data.base_value_low ?? null;
      const evHigh = data.ev_value_high ?? data.base_value_high ?? null;

      setValuation({
        loading: false,
        equityLow,
        equityHigh,
        evLow,
        evHigh,
        asOfDate: data.as_of_date ?? null,
        metricLabel: mapMetricLabel(data.metric_used),
      });
    } catch {
      setValuation(emptyValuation());
    }
  }, []);

  const fetchKoncernValuation = useCallback(async (companyIndex, q) => {
    if (!q || q.length !== 10) {
      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : { ...company, valuation: emptyValuation() }
        )
      );
      return;
    }

    setKoncernCompanies((prev) =>
      prev.map((company, idx) =>
        idx !== companyIndex
          ? company
          : { ...company, valuation: { ...company.valuation, loading: true } }
      )
    );

    try {
      const data = await getCompanyValuation(q);

      if (!data) {
        setKoncernCompanies((prev) =>
          prev.map((company, idx) =>
            idx !== companyIndex
              ? company
              : { ...company, valuation: emptyValuation() }
          )
        );
        return;
      }

      const equityLow =
        data.final_value_low ??
        data.equity_value_low ??
        data.ev_value_low ??
        data.base_value_low ??
        null;

      const equityHigh =
        data.final_value_high ??
        data.equity_value_high ??
        data.ev_value_high ??
        data.base_value_high ??
        null;

      const evLow = data.ev_value_low ?? data.base_value_low ?? null;
      const evHigh = data.ev_value_high ?? data.base_value_high ?? null;

      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : {
                ...company,
                valuation: {
                  loading: false,
                  equityLow,
                  equityHigh,
                  evLow,
                  evHigh,
                  asOfDate: data.as_of_date ?? null,
                  metricLabel: mapMetricLabel(data.metric_used),
                },
              }
        )
      );
    } catch {
      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : { ...company, valuation: emptyValuation() }
        )
      );
    }
  }, []);

  const runOrgCheck = useCallback(async (value) => {
    const q = onlyDigits(value);

    setHasAnnualReports(null);
    setAnnualReportsCount(0);
    setBvReportsRaw([]);
    setSyncStatus({ loading: false, msg: "", done: false });
    setFetchingFinancials(false);
    setFinancialsFetched(false);

    if (!q) {
      setOrgStatus({ loading: false, ok: false, msg: "" });
      setAnnualSlots((prev) => prev.map((s) => ({ ...s, fromBv: false })));
      setValuation(emptyValuation());
      return;
    }

    if (q.length !== 10) {
      setOrgStatus({ loading: false, ok: false, msg: "Ange 10 siffror." });
      setAnnualSlots((prev) => prev.map((s) => ({ ...s, fromBv: false })));
      setValuation(emptyValuation());
      return;
    }

    setOrgStatus({ loading: true, ok: false, msg: "Validerar org.nr…" });

    try {
      const data = await validateCompany(q);
      const ok = !!data?.organisation || Array.isArray(data?.organisationer);
      const name = extractValidationName(data, q);

      // BolagsAPI: check financials object instead of annualReports array
      const financials = data?.financials || data?.organisation?.financials || null;
      const reports = Array.isArray(data?.annualReports) ? data.annualReports : [];

      if (financials && typeof financials === "object") {
        // Mark slots as fetched based on financial years available from BolagsAPI
        const fyYears = [];
        if (financials.fiscal_years && Array.isArray(financials.fiscal_years)) {
          financials.fiscal_years.forEach((fy) => {
            const y = fy.year ?? (fy.end_date && String(fy.end_date).slice(0, 4));
            if (y) fyYears.push(parseInt(String(y), 10));
          });
        } else if (financials.fiscal_year_end) {
          // Single fiscal year info — at least mark the most recent year
          const y = String(financials.fiscal_year_end).slice(0, 4);
          if (y) fyYears.push(parseInt(y, 10));
        }
        if (fyYears.length) {
          const yearSet = new Set(fyYears);
          setAnnualSlots((prev) =>
            prev.map((slot) => ({
              ...slot,
              fromBv: yearSet.has(slot.year),
            }))
          );
          setHasAnnualReports(true);
          setAnnualReportsCount(fyYears.length);
        } else {
          setHasAnnualReports(null);
          setAnnualReportsCount(0);
        }
        setBvReportsRaw([]);
      } else if (reports.length) {
        // Legacy BV format fallback
        setHasAnnualReports(data?.hasAnnualReports ?? null);
        setAnnualReportsCount(reports.length);
        setBvReportsRaw(reports);
        markSlotsFromBv(reports);
      } else {
        setHasAnnualReports(null);
        setAnnualReportsCount(0);
        setBvReportsRaw([]);
      }

      // Set partOfGroup from BolagsAPI response
      setPartOfGroup(data?.organisation?.part_of_group || false);

      setOrgStatus({
        loading: false,
        ok,
        msg: ok ? `OK · ${name}` : "Organisation hittades inte.",
      });

      if (!ok) {
        setValuation(emptyValuation());
      }
    } catch (e) {
      const msg = e?.body?.detail || e?.message || "Validering misslyckades.";
      setOrgStatus({ loading: false, ok: false, msg });
      setHasAnnualReports(null);
      setAnnualReportsCount(0);
      setBvReportsRaw([]);
      setAnnualSlots((prev) => prev.map((s) => ({ ...s, fromBv: false })));
      setValuation(emptyValuation());
      setPartOfGroup(false);
    }
  }, [markSlotsFromBv]);

  const runKoncernOrgCheck = useCallback(async (companyIndex, value) => {
    const q = onlyDigits(value);

    setKoncernCompanies((prev) =>
      prev.map((company, idx) =>
        idx !== companyIndex
          ? company
          : {
              ...company,
              hasAnnualReports: null,
              annualReportsCount: 0,
              bvReportsRaw: [],
              syncStatus: { loading: false, msg: "", done: false },
            }
      )
    );

    if (!q) {
      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : {
                ...company,
                orgStatus: { loading: false, ok: false, msg: "" },
                valuation: emptyValuation(),
                annualSlots: company.annualSlots.map((s) => ({ ...s, fromBv: false })),
              }
        )
      );
      return;
    }

    if (q.length !== 10) {
      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : {
                ...company,
                orgStatus: { loading: false, ok: false, msg: "Ange 10 siffror." },
                valuation: emptyValuation(),
                annualSlots: company.annualSlots.map((s) => ({ ...s, fromBv: false })),
              }
        )
      );
      return;
    }

    setKoncernCompanies((prev) =>
      prev.map((company, idx) =>
        idx !== companyIndex
          ? company
          : {
              ...company,
              orgStatus: { loading: true, ok: false, msg: "Validerar org.nr…" },
            }
      )
    );

    try {
      const data = await validateCompany(q);
      const ok = !!data?.organisation || Array.isArray(data?.organisationer);
      const name = extractValidationName(data, q);

      // BolagsAPI: check financials object instead of annualReports array
      const financials = data?.financials || data?.organisation?.financials || null;
      const reports = Array.isArray(data?.annualReports) ? data.annualReports : [];

      let hasReports = data?.hasAnnualReports ?? null;
      let reportsCount = reports.length;

      if (financials && typeof financials === "object") {
        const fyYears = [];
        if (financials.fiscal_years && Array.isArray(financials.fiscal_years)) {
          financials.fiscal_years.forEach((fy) => {
            const y = fy.year ?? (fy.end_date && String(fy.end_date).slice(0, 4));
            if (y) fyYears.push(parseInt(String(y), 10));
          });
        } else if (financials.fiscal_year_end) {
          const y = String(financials.fiscal_year_end).slice(0, 4);
          if (y) fyYears.push(parseInt(y, 10));
        }
        if (fyYears.length) {
          const yearSet = new Set(fyYears);
          setKoncernCompanies((prev) =>
            prev.map((company, idx) =>
              idx !== companyIndex
                ? company
                : {
                    ...company,
                    annualSlots: company.annualSlots.map((slot) => ({
                      ...slot,
                      fromBv: yearSet.has(slot.year),
                    })),
                  }
            )
          );
          hasReports = true;
          reportsCount = fyYears.length;
        }
      } else if (reports.length) {
        markKoncernSlotsFromBv(companyIndex, reports);
      }

      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : {
                ...company,
                hasAnnualReports: hasReports,
                annualReportsCount: reportsCount,
                bvReportsRaw: reports,
                orgStatus: {
                  loading: false,
                  ok,
                  msg: ok ? `OK · ${name}` : "Organisation hittades inte.",
                },
              }
        )
      );

      if (!ok) {
        setKoncernCompanies((prev) =>
          prev.map((company, idx) =>
            idx !== companyIndex
              ? company
              : { ...company, valuation: emptyValuation() }
          )
        );
      }
    } catch (e) {
      const msg = e?.body?.detail || e?.message || "Validering misslyckades.";
      setKoncernCompanies((prev) =>
        prev.map((company, idx) =>
          idx !== companyIndex
            ? company
            : {
                ...company,
                orgStatus: { loading: false, ok: false, msg },
                hasAnnualReports: null,
                annualReportsCount: 0,
                bvReportsRaw: [],
                valuation: emptyValuation(),
                annualSlots: company.annualSlots.map((s) => ({ ...s, fromBv: false })),
              }
        )
      );
    }
  }, [markKoncernSlotsFromBv]);

  const scheduleOrgCheck = useCallback(
    (value) => {
      if (orgTimerRef.current) clearTimeout(orgTimerRef.current);
      orgTimerRef.current = setTimeout(() => runOrgCheck(value), ORG_DEBOUNCE_MS);
    },
    [runOrgCheck]
  );

  const scheduleKoncernOrgCheck = useCallback(
    (companyIndex, value) => {
      const currentTimer = koncernOrgTimersRef.current[companyIndex];
      if (currentTimer) clearTimeout(currentTimer);
      koncernOrgTimersRef.current[companyIndex] = setTimeout(
        () => runKoncernOrgCheck(companyIndex, value),
        ORG_DEBOUNCE_MS
      );
    },
    [runKoncernOrgCheck]
  );

  const lastValidatedOrgnr = useRef("");
  const handleOrgBlur = () => {
    if (orgTimerRef.current) clearTimeout(orgTimerRef.current);
    // Expand the dev alias here (on blur, once typing is done) so it
    // can't collide with the user typing the start of a real orgnr.
    // Only fires for whitelisted users — see fakeAliasEnabled.
    const alias = fakeAliasEnabled ? resolveFakeAlias(orgnr) : null;
    if (alias) {
      setOrgnr(alias);
      lastValidatedOrgnr.current = alias;
      runOrgCheck(alias);
      return;
    }
    const q = onlyDigits(orgnr);
    if (q === lastValidatedOrgnr.current) return; // no change, skip
    lastValidatedOrgnr.current = q;
    runOrgCheck(orgnr);
  };

  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    const q = onlyDigits(orgnr);
    if (q.length === 10 && q !== lastValidatedOrgnr.current) {
      prefilledRef.current = true;
      lastValidatedOrgnr.current = q;
      runOrgCheck(orgnr);
    }
  }, [orgnr, runOrgCheck]);

  const handleKoncernOrgBlur = (companyIndex) => {
    if (koncernOrgTimersRef.current[companyIndex]) {
      clearTimeout(koncernOrgTimersRef.current[companyIndex]);
    }
    runKoncernOrgCheck(companyIndex, koncernCompanies[companyIndex]?.orgnr || "");
  };

  const handleWebsiteBlur = () => {
    const { normalized, ok, msg } = normalizeWebsite(website);
    setWebStatus({ ok, msg });
    if (ok) setWebsite(normalized);
  };

  const handleKoncernWebsiteBlur = (companyIndex) => {
    setKoncernCompanies((prev) =>
      prev.map((company, idx) => {
        if (idx !== companyIndex) return company;
        const { normalized, ok, msg } = normalizeWebsite(company.website);
        return {
          ...company,
          website: ok ? normalized : company.website,
          webStatus: { ok, msg },
        };
      })
    );
  };

  const handleBokslutPick = (slotIndex, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const file = files[0];
    setAnnualSlots((prev) => {
      const next = [...prev];
      const slot = next[slotIndex];
      next[slotIndex] = { ...slot, file: renameWithPrefix(file, "bokslut", slot.year) };
      return next;
    });
    e.target.value = "";
  };

  const handleSiePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const file = files[0];
    setSieFile(renameWithPrefix(file, "sie", "lopande"));
    e.target.value = "";
  };

  const handleKoncernBokslutPick = (companyIndex, slotIndex, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const file = files[0];

    setKoncernCompanies((prev) =>
      prev.map((company, idx) => {
        if (idx !== companyIndex) return company;
        const nextSlots = [...company.annualSlots];
        const slot = nextSlots[slotIndex];
        nextSlots[slotIndex] = {
          ...slot,
          file: renameWithPrefix(file, "bokslut", slot.year),
        };
        return { ...company, annualSlots: nextSlots };
      })
    );

    e.target.value = "";
  };

  const handleKoncernSiePick = (companyIndex, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const file = files[0];

    setKoncernCompanies((prev) =>
      prev.map((company, idx) =>
        idx !== companyIndex
          ? company
          : { ...company, sieFile: renameWithPrefix(file, "sie", "lopande") }
      )
    );

    e.target.value = "";
  };

  const handleKoncernDropAnnual = (companyIndex, slotIndex, e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    const file = files[0];

    setKoncernCompanies((prev) =>
      prev.map((company, idx) => {
        if (idx !== companyIndex) return company;
        const nextSlots = [...company.annualSlots];
        const slot = nextSlots[slotIndex];
        nextSlots[slotIndex] = {
          ...slot,
          file: renameWithPrefix(file, "bokslut", slot.year),
        };
        return { ...company, annualSlots: nextSlots };
      })
    );
  };

  const handleKoncernDropSie = (companyIndex, e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    const file = files[0];

    setKoncernCompanies((prev) =>
      prev.map((company, idx) =>
        idx !== companyIndex
          ? company
          : { ...company, sieFile: renameWithPrefix(file, "sie", "lopande") }
      )
    );
  };

  useEffect(() => {
    if (!orgnr) {
      setOrgStatus({ loading: false, ok: false, msg: "" });
      setHasAnnualReports(null);
      setAnnualReportsCount(0);
      setBvReportsRaw([]);
      setAnnualSlots((prev) => prev.map((s) => ({ ...s, fromBv: false })));
      setValuation(emptyValuation());
      setPartOfGroup(false);
      setIsKoncern(false);
      return;
    }
    scheduleOrgCheck(orgnr);
    return () => orgTimerRef.current && clearTimeout(orgTimerRef.current);
  }, [orgnr, scheduleOrgCheck]);

  useEffect(() => {
    koncernCompanies.forEach((company, index) => {
      if (company.orgnr) {
        scheduleKoncernOrgCheck(index, company.orgnr);
      }
    });

    return () => {
      Object.values(koncernOrgTimersRef.current).forEach((timer) => clearTimeout(timer));
    };
  }, [koncernCompanies.map((c) => c.orgnr).join("|"), scheduleKoncernOrgCheck]); // eslint-disable-line react-hooks/exhaustive-deps

  const addKoncernCompany = () => {
    setKoncernCompanies((prev) => {
      const next = [...prev, createKoncernCompany()];
      setActiveKoncernIndex(next.length - 1);
      return next;
    });
  };

  const removeKoncernCompany = (index) => {
    setKoncernCompanies((prev) => {
      const next = prev.filter((_, idx) => idx !== index);

      if (!next.length) {
        setActiveKoncernIndex(null);
      } else if (activeKoncernIndex === index) {
        setActiveKoncernIndex(Math.max(0, index - 1));
      } else if (activeKoncernIndex > index) {
        setActiveKoncernIndex(activeKoncernIndex - 1);
      }

      return next;
    });
  };

  const updateKoncernField = (index, field, value) => {
    setKoncernCompanies((prev) =>
      prev.map((company, idx) => {
        if (idx !== index) return company;

        if (field === "orgnr") {
          return {
            ...company,
            orgnr: value,
            orgStatus: { loading: false, ok: false, msg: "" },
            hasAnnualReports: null,
            annualReportsCount: 0,
            bvReportsRaw: [],
            valuation: emptyValuation(),
            annualSlots: company.annualSlots.map((s) => ({ ...s, fromBv: false })),
            syncStatus: { loading: false, msg: "", done: false },
          };
        }

        if (field === "website") {
          return {
            ...company,
            website: value,
            webStatus: { ok: true, msg: "" },
          };
        }

        return company;
      })
    );
  };

  async function handleNext() {
    if (!canProceed || processing) return;

    const q = onlyDigits(orgnr);
    if (q.length !== 10) return;

    setProcessing(true);
    setKoncernError("");
    setKoncernDone([]);

    let workspaceSlug = null;

    try {
      // ── Step 1: Create workspace for main company ──
      setProcessMsg("Skapar workspace…");
      const ensureResp = await ensureWorkspace(q, { credentials: "include" });
      // Capture slug from the response so we redirect to the new URL key
      // instead of /dashboard/<orgnr>. Two broker tenants on the same
      // orgnr would otherwise both land on identical URLs.
      try {
        const body = ensureResp?.body || ensureResp?.data || ensureResp;
        workspaceSlug = body?.workspace_slug || null;
      } catch {
        workspaceSlug = null;
      }

      // ── Step 2: Upload any manual files (SIE, bokslut) ──
      const form = new FormData();
      form.append("orgnr", q);
      const mainWebsite = normalizeWebsite(website);
      if (website && mainWebsite.ok && mainWebsite.normalized) {
        form.append("website", mainWebsite.normalized);
      }
      if (sieFile) form.append("sie_lopande", sieFile);
      annualSlots.forEach((slot) => {
        if (slot?.file) form.append(`bokslut_${slot.year}`, slot.file);
      });

      const hasFiles = sieFile || annualSlots.some((s) => !!s.file);
      if (hasFiles) {
        setProcessMsg("Laddar upp filer…");
        await syncCompany(q, form, { credentials: "include" });
      }

      // ── Step 3: Koncern — add subsidiaries + sync their files ──
      if (isKoncern && koncernCompanies.length) {
        setProcessMsg("Lägger till koncernbolag…");
        setKoncernAdding(true);

        const koncernForm = new FormData();

        // Subsidiaries with indexed prefix
        koncernCompanies.forEach((company, idx) => {
          const subOrgnr = onlyDigits(company.orgnr);
          if (subOrgnr.length !== 10) return;
          koncernForm.append(`sub_${idx}_orgnr`, subOrgnr);
          const subWeb = normalizeWebsite(company.website);
          if (company.website && subWeb.ok && subWeb.normalized) {
            koncernForm.append(`sub_${idx}_website`, subWeb.normalized);
          }
          if (company.sieFile) koncernForm.append(`sub_${idx}_sie_lopande`, company.sieFile);
          company.annualSlots.forEach((slot) => {
            if (slot?.file) koncernForm.append(`sub_${idx}_bokslut_${slot.year}`, slot.file);
          });
        });

        try {
          const res = await fetch(`${API_BASE}/processing/workspace/${q}/sync-koncern/`, {
            method: "POST",
            credentials: "include",
            headers: { "X-CSRFToken": getCsrfToken() },
            body: koncernForm,
          });
          const data = await res.json().catch(() => ({}));

          const added = Object.values(data.subsidiaries || {})
            .filter((r) => r.ok)
            .map((r) => r.orgnr)
            .filter(Boolean);
          const errors = Object.values(data.subsidiaries || {})
            .filter((r) => !r.ok)
            .map((r) => `${r.orgnr || "?"}: ${r.error || "fel"}`);

          setKoncernDone(added);
          if (errors.length) setKoncernError(errors.join(" | "));
        } catch (e) {
          setKoncernError(e?.message || "Koncernsynk misslyckades.");
        }
        setKoncernAdding(false);
      }

      // ── Step 4: Sync financials from BolagsAPI data already saved ──
      setProcessMsg("Synkar finansiell data…");
      try {
        // The AnnualReportYear records were already created by "Hämta data"
        // Just trigger valuation recompute
        await request(`/valuation/workspace/${q}/koncern/`, { method: "POST" }).catch(() => null);
      } catch {
        // Non-critical — valuation will be computed on first visit
      }

      setProcessMsg("Klart. Öppnar plattformen…");
      setTimeout(() => {
        setProcessing(false);
        // Prefer the workspace slug when ensure_workspace returned one
        // — falls back to orgnr only for pre-slug rows or older API.
        const urlKey = workspaceSlug || q;
        window.location.href = `/dashboard/${urlKey}`;
      }, 400);
    } catch (e) {
      const msg =
        e?.body?.detail || e?.body?.error || e?.message || "Synk/Uppladdning misslyckades. Försök igen.";
      setProcessMsg(msg);
      setKoncernAdding(false);
      setTimeout(() => setProcessing(false), 1400);
    }
  }

  const handleContinue = () => {
    window.location.href = "/dashboard";
  };

  const renderCheckStep = () => {
    let annualSummary = "";
    if (availableAnnualCount >= 3) {
      annualSummary = "Vi har minst 3 bokslut – det ger ett bättre underlag för värderingen.";
    } else if (availableAnnualCount === 2) {
      annualSummary = "Vi har 2 bokslut – det räcker för en preliminär värdering, men spannet kan bli något bredare.";
    } else if (availableAnnualCount === 1) {
      annualSummary = "Vi har 1 bokslut – det räcker för en första indikation, men utan historik kan värderingen variera mer.";
    } else {
      annualSummary = "Klicka \"Hämta data\" ovan eller släpp filer i rutorna nedan.";
    }

    const webHint =
      !website
        ? "Valfritt. Vi kontrollerar formatet när du lämnar fältet."
        : webStatus.ok
          ? webStatus.msg || "OK (format)."
          : webStatus.msg || "Ogiltig webbadress (format). Du kan ändå fortsätta.";

    return (
      <div className="checkcompany-card">
        <div className="checkcompany-header-row">
          <div>
            <h1 className="checkcompany-title">Bokslut &amp; finansiell data</h1>
            <p className="checkcompany-subtitle-main">
              Ange organisationsnummer och klicka "Hämta data" för att hämta bokslut. Du kan även ladda upp egna filer.
              (SIE är valfritt.)
            </p>
          </div>
          <span className="checkcompany-step-pill">Steg 1 av 3</span>
        </div>

        {/* Manual koncern trigger — broker opts in when multiple group
            companies are being sold together. Replaces the previous
            auto-detection (which required an upstream group-API call). */}
        {!showGroupPicker && !isKoncern && orgStatus.ok && (
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => {
                setGroupSuggestions([]);
                setShowGroupPicker(true);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                background: "#F5F5F4",
                color: "#1C1917",
                border: "1px solid #E7E5E4",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              <svg width="16" height="16" fill="none" stroke="#57534E" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 01-9 9"/>
              </svg>
              Flera bolag i koncernen ska säljas
            </button>
          </div>
        )}

        {/* ── Group picker — opens via the manual trigger above ── */}
        {showGroupPicker && (
          <div className="checkcompany-block" style={{
            background: "#F5F5F4",
            border: "1px solid #E7E5E4",
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <svg width="18" height="18" fill="none" stroke="#57534E" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 01-9 9"/>
              </svg>
              <strong style={{ fontSize: 14, color: "#1C1917" }}>Bolag i koncernen som ska säljas</strong>
            </div>
            <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 12, lineHeight: 1.5 }}>
              {groupSuggestions.length > 0
                ? "Välj vilka bolag som ska inkluderas i analysen. Saknas något bolag? Lägg till det manuellt nedan."
                : "Lägg till varje bolag i koncernen som ingår i försäljningen med dess organisationsnummer."}
            </p>
            {(() => {
              const toggleIdx = (idx) =>
                setGroupSuggestions((prev) =>
                  prev.map((g, i) => (i === idx ? { ...g, checked: !g.checked } : g))
                );

              const sections = [
                { key: "parent", label: "Moderbolag", hint: "Direkt eller yttersta moderbolag" },
                { key: "sibling", label: "Systerbolag", hint: "Samma moderbolag" },
                { key: "child", label: "Dotterbolag", hint: "Direkta dotterbolag" },
                { key: "other", label: "Övriga koncernbolag", hint: "Djupare nivåer i koncernen" },
              ];

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {sections.map((sec) => {
                    const entries = groupSuggestions
                      .map((s, idx) => ({ s, idx }))
                      .filter(({ s }) => s.role === sec.key);
                    if (entries.length === 0) return null;
                    return (
                      <div
                        key={sec.key}
                        style={{
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 8,
                            marginBottom: 8,
                            paddingBottom: 6,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          <strong style={{ fontSize: 13, color: "#1C1917" }}>{sec.label}</strong>
                          <span style={{ fontSize: 11, color: "#9ca3af" }}>
                            {sec.hint} · {entries.length} st
                          </span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {entries.map(({ s, idx }) => (
                            <label
                              key={s.orgnr}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "6px 8px",
                                borderRadius: 6,
                                fontSize: 13,
                                cursor: "pointer",
                                background: s.checked ? "#FAFAF9" : "transparent",
                              }}
                              onMouseEnter={(e) => {
                                if (!s.checked) e.currentTarget.style.background = "#FAFAF9";
                              }}
                              onMouseLeave={(e) => {
                                if (!s.checked) e.currentTarget.style.background = "transparent";
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={s.checked}
                                onChange={() => toggleIdx(idx)}
                              />
                              <span style={{ fontWeight: 600 }}>{s.name}</span>
                              <span style={{ color: "#9ca3af" }}>{s.orgnr}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* Manual add by orgnr */}
            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Lägg till med org.nr"
                style={{
                  flex: 1, padding: "8px 12px", fontSize: 13,
                  border: "1px solid #d1d5db", borderRadius: 8,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const nr = onlyDigits(e.target.value);
                    if (nr.length === 10 && !groupSuggestions.find((s) => s.orgnr === nr)) {
                      setGroupSuggestions((prev) => [...prev, { orgnr: nr, name: nr, checked: true, role: "other" }]);
                      e.target.value = "";
                    }
                  }
                }}
              />
              <button
                type="button"
                style={{
                  padding: "8px 12px", fontSize: 13, fontWeight: 600,
                  background: "#e5e7eb", color: "#374151", border: "none",
                  borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap",
                }}
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling;
                  const nr = onlyDigits(input.value);
                  if (nr.length === 10 && !groupSuggestions.find((s) => s.orgnr === nr)) {
                    setGroupSuggestions((prev) => [...prev, { orgnr: nr, name: nr, checked: true, role: "other" }]);
                    input.value = "";
                  }
                }}
              >
                + Lägg till
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                style={{
                  padding: "8px 16px", fontSize: 13, fontWeight: 600,
                  background: "#57534E", color: "#fff", border: "none",
                  borderRadius: 8, cursor: "pointer",
                }}
                onClick={async () => {
                  const selected = groupSuggestions.filter((s) => s.checked);
                  if (selected.length) {
                    setIsKoncern(true);
                    const newCompanies = selected.map((s) => {
                      const sub = createKoncernCompany();
                      sub.orgnr = s.orgnr;
                      sub.orgStatus = { loading: true, ok: false, msg: `Hämtar ${s.name}…` };
                      return sub;
                    });
                    setKoncernCompanies(newCompanies);
                    setActiveKoncernIndex(0);
                    setShowGroupPicker(false);

                    // Fetch financials for each selected member
                    for (let idx = 0; idx < newCompanies.length; idx++) {
                      const memberOrgnr = newCompanies[idx].orgnr;
                      try {
                        const memberFin = await getCompanyFinancials(memberOrgnr).catch(() => null);
                        const slots = (memberFin?.years || []).slice(0, 5).map((y) => {
                          const end = y.fiscal_year_end || "";
                          const yr = parseInt(end.slice(0, 4), 10);
                          const st = y._status || (y._parse_method === "api" ? "parsed" : y._parse_method === "pending" ? "fetched" : y._parse_method === "empty" ? "empty" : "available");
                          return {
                            year: yr,
                            fromBv: st === "parsed" || st === "fetched",
                            status: st,
                            file: null,
                          };
                        });
                        const filledCount = slots.filter((s) => s.fromBv).length;

                        setKoncernCompanies((prev) =>
                          prev.map((c, i) =>
                            i !== idx ? c : {
                              ...c,
                              orgStatus: { loading: false, ok: true, msg: `OK · ${selected[idx].name}` },
                              annualSlots: slots,
                              hasAnnualReports: filledCount > 0,
                              annualReportsCount: filledCount,
                            }
                          )
                        );
                      } catch {
                        setKoncernCompanies((prev) =>
                          prev.map((c, i) =>
                            i !== idx ? c : {
                              ...c,
                              orgStatus: { loading: false, ok: true, msg: `OK · ${selected[idx].name}` },
                            }
                          )
                        );
                      }
                    }
                  } else {
                    setShowGroupPicker(false);
                  }
                }}
              >
                Inkludera valda ({groupSuggestions.filter((s) => s.checked).length})
              </button>
              <button
                type="button"
                style={{
                  padding: "8px 16px", fontSize: 13, fontWeight: 600,
                  background: "#f3f4f6", color: "#374151", border: "none",
                  borderRadius: 8, cursor: "pointer",
                }}
                onClick={() => setShowGroupPicker(false)}
              >
                Hoppa över
              </button>
            </div>
          </div>
        )}


        {/* ── Main company: compact summary OR full form ── */}
        {isKoncern && mainCompacted ? (
          <CompactSummary
            label="Moderbolag"
            orgnr={onlyDigits(orgnr)}
            orgStatusMsg={orgStatus.msg}
            annualSlots={annualSlots}
            sieFile={sieFile}
            website={website}
            onClick={() => setMainCompacted(false)}
          />
        ) : (
          <>
            <div className="checkcompany-block">
              <label htmlFor="orgnr" className="checkcompany-label">
                Organisationsnummer
              </label>
              <div className="checkcompany-row">
                <input
                  id="orgnr"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  placeholder="t.ex. 5562820745"
                  value={orgnr}
                  onChange={(e) => {
                    const val = onlyDigits(e.target.value);
                    setOrgnr(val);
                    if (val !== lastValidatedOrgnr.current) {
                      setOrgStatus({ loading: false, ok: false, msg: "" });
                      setHasAnnualReports(null);
                      setAnnualReportsCount(0);
                      setBvReportsRaw([]);
                      setSyncStatus({ loading: false, msg: "", done: false });
                      setValuation(emptyValuation());
                      setFinancialsFetched(false);
                      setFetchingFinancials(false);
                    }
                  }}
                  onBlur={handleOrgBlur}
                  className="checkcompany-input"
                />
              </div>
              <small className={`checkcompany-small ${orgStatus.ok ? "checkcompany-ok" : ""}`}>
                {orgStatus.loading ? "Validerar org.nr…" : orgStatus.msg || "Vi validerar data."}
              </small>
            </div>

            <div className="checkcompany-block">
              <label htmlFor="website" className="checkcompany-label">
                Webbplats (valfritt)
              </label>
              <div className="checkcompany-row">
                <input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  value={website}
                  onChange={(e) => {
                    setWebsite(e.target.value);
                    setWebStatus({ ok: true, msg: "" });
                  }}
                  onBlur={handleWebsiteBlur}
                  className="checkcompany-input"
                />
              </div>
              <small className={`checkcompany-small ${website && webStatus.ok ? "checkcompany-ok" : ""}`}>
                {webHint}
              </small>
            </div>

            <div className="checkcompany-block">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <h2 className="checkcompany-subheading" style={{ margin: 0 }}>Bokslut – senaste 5 räkenskapsår</h2>
                {orgStatus.ok && (
                  <button
                    type="button"
                    style={{
                      padding: "8px 20px",
                      fontSize: 13,
                      fontWeight: 600,
                      border: "none",
                      borderRadius: 8,
                      cursor: financialsFetched ? "default" : "pointer",
                      background: financialsFetched ? "#dcfce7" : "#f3f4f6",
                      color: financialsFetched ? "#16a34a" : "#374151",
                      transition: "all 0.15s",
                    }}
                    onClick={!financialsFetched ? handleFetchFinancials : undefined}
                    disabled={fetchingFinancials || financialsFetched}
                    onMouseOver={(e) => { if (!financialsFetched && !fetchingFinancials) e.currentTarget.style.background = "#e5e7eb"; }}
                    onMouseOut={(e) => { if (!financialsFetched) e.currentTarget.style.background = "#f3f4f6"; }}
                  >
                    {fetchingFinancials ? "Hämtar…" : financialsFetched ? "✓ Data hämtad" : "Hämta data"}
                  </button>
                )}
              </div>
              <p className="checkcompany-small">{fetchHint || fetchError || annualSummary}</p>

              {upstreamError && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 14px",
                    marginBottom: 12,
                    background: "#FEF2F2",
                    border: "1px solid #FCA5A5",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "#B91C1C",
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
                  <div>
                    {(() => {
                      const u = typeof upstreamError === "object" ? upstreamError : null;
                      const code = u?.code;
                      if (code === "not_found")    return u.detail || "Bolaget hittades inte hos leverantören.";
                      if (code === "rate_limit")   return u.detail || "Leverantören har nått sin gräns. Försök igen om en stund.";
                      if (code === "plan_limited") return u.detail || "Funktionen ingår inte i nuvarande prenumeration.";
                      if (code === "auth")         return u.detail || "Leverantörens API-nyckel saknas eller är felaktig.";
                      // Fallback for generic upstream errors / true boolean.
                      return u?.detail || "Fel hos leverantör, testa ett annat org. nr.";
                    })()}
                  </div>
                </div>
              )}

              {ocrInProgress && pendingOcrCount > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 14px",
                    marginBottom: 12,
                    background: "#FFFBEB",
                    border: "1px solid #FDE68A",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "#78350F",
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>⚙️</span>
                  <div>
                    <strong>{pendingOcrCount}</strong> {pendingOcrCount === 1 ? "bokslut är skannat och bearbetas" : "bokslut är skannade och bearbetas"} i bakgrunden.
                    Du kan fortsätta — siffrorna fylls i automatiskt när tolkningen är klar.
                  </div>
                </div>
              )}

              {yearsAvailable !== null && yearsAvailable > 0 && yearsAvailable < 5 && pendingOcrCount === 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 14px",
                    marginBottom: 12,
                    background: "#FEFCE8",
                    border: "1px solid #FDE68A",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "#78350F",
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>ℹ️</span>
                  <div>
                    Endast <strong>{yearsAvailable}</strong> bokslut hittades för detta bolag.
                    Bolaget kan vara nystartat eller ha färre publicerade årsredovisningar än vanligt.
                    Du kan ladda upp egna filer för äldre år om det behövs.
                  </div>
                </div>
              )}

              <div className="checkcompany-annual-grid" style={{ position: "relative" }}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                {fetchingFinancials && (
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(255,255,255,0.85)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 12,
                    zIndex: 5,
                    borderRadius: 8,
                  }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      border: "3px solid #e5e7eb",
                      borderTopColor: "#57534E",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }} />
                    <div style={{ fontSize: 14, color: "#57534E", fontWeight: 500 }}>
                      Hämtar bokslut…
                    </div>
                  </div>
                )}
                {annualSlots.map((slot, index) => {
                  const st = slot.status || (slot.fromBv ? "parsed" : "empty");
                  const filled = st === "parsed" || st === "fetched" || !!slot.file;
                  const isParsed = st === "parsed" || !!slot.file;
                  const isPending = st === "fetched" && !slot.file;
                  const isEmpty = (st === "empty" || st === "available") && !slot.file;

                  const slotClass = `checkcompany-slot${
                    isParsed ? " checkcompany-slot--filled" :
                    isPending ? " checkcompany-slot--pending" :
                    ""
                  }`;

                  return (
                    <div
                      key={slot.year}
                      className={slotClass}
                      style={isPending ? { borderColor: "#f59e0b", background: "#fffbeb" } : isEmpty ? { borderColor: "#d1d5db", background: "#f9fafb", opacity: 0.7 } : undefined}
                      onClick={() => { if (!filled) fileInputRefs.current[index]?.click(); }}
                      onDragOver={(e) => { if (filled) return; e.preventDefault(); e.currentTarget.classList.add("checkcompany-slot-dragover"); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove("checkcompany-slot-dragover"); }}
                      onDrop={(e) => {
                        if (isParsed) return;
                        e.preventDefault();
                        e.currentTarget.classList.remove("checkcompany-slot-dragover");
                        const files = Array.from(e.dataTransfer?.files || []);
                        if (!files.length) return;
                        setAnnualSlots((prev) => {
                          const next = [...prev];
                          next[index] = { ...next[index], file: renameWithPrefix(files[0], "bokslut", next[index].year), status: "parsed" };
                          return next;
                        });
                      }}
                    >
                      <div className="checkcompany-slot-header">
                        <span className="checkcompany-slot-title">Bokslut {slot.year}</span>
                        {isParsed && <span className="checkcompany-slot-tag">Hämtat</span>}
                        {isPending && <span className="checkcompany-slot-tag" style={{ background: "#fef3c7", color: "#92400e" }}>{ocrInProgress ? "Tolkar…" : "Behöver tolkas"}</span>}
                        {isEmpty && <span className="checkcompany-slot-tag" style={{ background: "#f3f4f6", color: "#6b7280" }}>Saknas</span>}
                      </div>
                      {isParsed && <div className="checkcompany-slot-check">✓</div>}
                      {isPending && ocrInProgress && (
                        <div className="checkcompany-slot-check" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                          <div style={{
                            width: 28,
                            height: 28,
                            border: "3px solid #fde68a",
                            borderTopColor: "#f59e0b",
                            borderRadius: "50%",
                            animation: "spin 0.8s linear infinite",
                          }} />
                        </div>
                      )}
                      {isPending && !ocrInProgress && <div className="checkcompany-slot-check" style={{ color: "#f59e0b" }}>⏳</div>}
                      {isEmpty && <div className="checkcompany-slot-body"><span className="checkcompany-slot-instruction" style={{ color: "#9ca3af" }}>Ladda upp manuellt</span></div>}
                      {!filled && !isEmpty && <div className="checkcompany-slot-body"><span className="checkcompany-slot-instruction">Släpp fil här eller klicka</span></div>}
                      <input
                        ref={(el) => (fileInputRefs.current[index] = el)}
                        type="file"
                        accept=".pdf,.zip,.doc,.docx,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={(e) => handleBokslutPick(index, e)}
                      />
                    </div>
                  );
                })}

                <div
                  className={`checkcompany-slot checkcompany-slot--sie ${sieFile ? "checkcompany-slot--filled" : ""}`}
                  onClick={() => { if (!sieFile) sieInputRef.current?.click(); }}
                  onDragOver={(e) => { if (sieFile) return; e.preventDefault(); e.currentTarget.classList.add("checkcompany-slot-dragover"); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove("checkcompany-slot-dragover"); }}
                  onDrop={(e) => {
                    if (sieFile) return;
                    e.preventDefault();
                    e.currentTarget.classList.remove("checkcompany-slot-dragover");
                    const files = Array.from(e.dataTransfer?.files || []);
                    if (files.length) setSieFile(renameWithPrefix(files[0], "sie", "lopande"));
                  }}
                >
                  <div className="checkcompany-slot-header">
                    <span className="checkcompany-slot-title">SIE-fil (senaste 12 mån)</span>
                    <span className="checkcompany-slot-tag checkcompany-slot-tag--optional">Valfritt</span>
                  </div>
                  {!sieFile && (
                    <div className="checkcompany-slot-body">
                      <span className="checkcompany-slot-instruction">
                        Släpp SIE här eller klicka — annars efterfrågas filen från säljaren i säljarportalen.
                      </span>
                    </div>
                  )}
                  {sieFile && <div className="checkcompany-slot-check">✓</div>}
                  <input ref={sieInputRef} type="file" accept=".sie,.se,.si" style={{ display: "none" }} onChange={handleSiePick} />
                </div>
              </div>
            </div>

            {/* Gray full-width button to compact main company and start adding subsidiaries */}
            {isKoncern && (
              <button
                type="button"
                className="checkcompany-add-company-btn"
                onClick={() => {
                  setMainCompacted(true);
                  if (!koncernCompanies.length) {
                    setKoncernCompanies([createKoncernCompany()]);
                    setActiveKoncernIndex(0);
                  }
                }}
              >
                + Lägg till dotterbolag
              </button>
            )}
          </>
        )}

        {/* ── Subsidiary accordions (shown once main is compacted) ── */}
        {isKoncern && mainCompacted && (
          <>
            <div className="checkcompany-koncern-list">
              {koncernCompanies.map((company, index) => (
                <CompanyAccordion
                  key={company.id}
                  company={company}
                  index={index}
                  active={activeKoncernIndex === index}
                  onToggle={() => setActiveKoncernIndex((prev) => (prev === index ? null : index))}
                  onChangeField={updateKoncernField}
                  onOrgBlur={handleKoncernOrgBlur}
                  onWebsiteBlur={handleKoncernWebsiteBlur}
                  onBokslutPick={handleKoncernBokslutPick}
                  onSiePick={handleKoncernSiePick}
                  onDropAnnual={handleKoncernDropAnnual}
                  onDropSie={handleKoncernDropSie}
                  onRemove={removeKoncernCompany}
                  canRemove={koncernCompanies.length > 1}
                  isLast={index === koncernCompanies.length - 1}
                  onAddNext={addKoncernCompany}
                  fileInputRefs={koncernFileInputRefs}
                  sieInputRefs={koncernSieInputRefs}
                />
              ))}
            </div>

            {koncernDone.length > 0 && (
              <p className="checkcompany-koncern-ok" style={{ marginTop: 10 }}>
                Tillagda: {koncernDone.join(", ")}
              </p>
            )}
            {koncernError && (
              <p className="checkcompany-koncern-error" style={{ marginTop: 10 }}>
                {koncernError}
              </p>
            )}
          </>
        )}

        <div className="checkcompany-footer">
          <span className="checkcompany-status">
            {orgStatus.ok ? "✓ Org.nr OK" : "• Org.nr saknas"} ·{" "}
            {website ? (webStatus.ok ? "✓ Webb (format OK)" : "• Webb (format fel – men valfritt)") : "• Webb ej angiven"} ·{" "}
            {availableAnnualCount >= MIN_FOR_PRELIM ? "✓ Minst 1 bokslut (gärna 3+)" : "• Minst 1 bokslut krävs"} ·{" "}
            {sieFile ? "✓ SIE vald" : "• SIE valfri"}
          </span>
          <button
            onClick={handleNext}
            disabled={!canProceed || processing}
            className={`checkcompany-next ${canProceed ? "checkcompany-next--enabled" : ""}`}
          >
            Fortsätt
          </button>
        </div>
      </div>
    );
  };

  const renderOverview = (minimal = false) => {
    const cleanOrgnr = onlyDigits(orgnr);

    const valueItems = [
      {
        id: "main",
        title: nameFromStatusMsg(orgStatus.msg) || "Moderbolag",
        subtitle: "Moderbolag",
        orgnr: cleanOrgnr,
        valuation,
      },
      ...koncernCompanies
        .filter((company) => onlyDigits(company.orgnr).length === 10)
        .map((company, index) => ({
          id: company.id,
          title: nameFromStatusMsg(company.orgStatus.msg) || `Dotterbolag ${index + 1}`,
          subtitle: `Dotterbolag ${index + 1}`,
          orgnr: onlyDigits(company.orgnr),
          valuation: company.valuation,
        })),
    ];

    const hasKoncern = isKoncern && valueItems.length > 1;

    const totalEquityLow = valueItems.reduce(
      (sum, item) => sum + (item.valuation.equityLow || 0),
      0
    );
    const totalEquityHigh = valueItems.reduce(
      (sum, item) => sum + (item.valuation.equityHigh || 0),
      0
    );
    const totalEvLow = valueItems.reduce(
      (sum, item) => sum + (item.valuation.evLow || 0),
      0
    );
    const totalEvHigh = valueItems.reduce(
      (sum, item) => sum + (item.valuation.evHigh || 0),
      0
    );

    let historyNote = "";
    if (availableAnnualCount === 1) {
      historyNote = " Eftersom vi bara har ett bokslut saknas historik, så värderingsspannet kan variera mer än normalt.";
    } else if (availableAnnualCount === 2) {
      historyNote = " Vi har viss historik, men ytterligare bokslut skulle göra spannet mer träffsäkert.";
    }

    return (
      <div className="checkcompany-card checkcompany-card--choice">
        <div className="checkcompany-header-row">
          <div>
            <h1 className="checkcompany-title">
              {hasKoncern ? "Preliminär koncernvärdering" : "Din preliminära värdering"}
            </h1>
            <p className="checkcompany-subtitle-main">
              {hasKoncern
                ? `Det här är en första indikation för hela koncernen baserad på moderbolaget och tillagda dotterbolag.${historyNote}`
                : `Det här är en första indikation baserad på era bokslut.${historyNote} ${
                    minimal
                      ? "Nästa steg är att gå in i plattformen."
                      : "Nästa steg är att antingen gå in i plattformen eller svara på några frågor för en mer träffsäker bild."
                  }`}
            </p>
          </div>
          {!minimal && <span className="checkcompany-step-pill">Steg 2 av 3</span>}
        </div>

        <div className="checkcompany-choice-layout">
          <section className="checkcompany-valuationbox">
            <div className="checkcompany-val-pill">
              {hasKoncern ? "Preliminärt koncernvärde" : "Preliminär bolagsvärdering"}
            </div>

            {/* ── Overview totals (always shown) ── */}
            <div className="checkcompany-val-main">
              <span className="checkcompany-val-label">
                {hasKoncern ? "Samlat Equity value" : "Equity value"}
              </span>
              <p className="checkcompany-val-range">
                {hasKoncern
                  ? formatRange(totalEquityLow, totalEquityHigh)
                  : formatRange(valuation.equityLow, valuation.equityHigh)}
              </p>
            </div>

            <p className="checkcompany-val-meta">
              {hasKoncern
                ? "Summerat spann för moderbolag och dotterbolag."
                : valuation.asOfDate
                  ? `Baserat på bokslut per ${new Date(valuation.asOfDate).toLocaleDateString("sv-SE")}`
                  : "Baserat på senaste tillgängliga bokslut"}
              {!hasKoncern && valuation.metricLabel
                ? ` · nyckeltal: ${valuation.metricLabel.toLowerCase()}`
                : ""}
            </p>

            <div className="checkcompany-val-divider" />

            <div className="checkcompany-ev-section">
              <div className="checkcompany-ev-header">
                <span className="checkcompany-ev-label">Enterprise Value (EV)</span>
                <span className="checkcompany-ev-range">
                  {hasKoncern
                    ? formatRange(totalEvLow, totalEvHigh)
                    : formatRange(valuation.evLow, valuation.evHigh)}
                </span>
              </div>
              <p className="checkcompany-ev-help">
                {hasKoncern
                  ? "Summerat spann. Synergier och avdrag mellan bolagen är ännu inte justerade."
                  : "EV är värdet före kassa och räntebärande skulder. Equity value är det spann som typiskt är relevant vid en försäljning."}
              </p>
            </div>

            <p className="checkcompany-val-help">
              {hasKoncern
                ? "Öppna respektive bolag nedan för att se individuella spann."
                : "Värderingen är preliminär och baserad på historiska bokslut. När vi kompletterar med riskprofil, kundmix och tillväxt kan vi kalibrera multipeln."}
            </p>

            <Link to={`/duediligence/${cleanOrgnr}`} className="checkcompany-val-complete-link">
              Slutför genomlysningen för att låsa upp slutvärderingen →
            </Link>

            {/* ── Per-company accordions — only when multiple companies ── */}
            {hasKoncern && (
              <div className="checkcompany-koncern-list" style={{ marginTop: 16 }}>
                {valueItems.map((item) => {
                  const isOpen = openValuationId === item.id;
                  return (
                    <div key={item.id} className="checkcompany-koncern-accordion">
                      <button
                        type="button"
                        className="checkcompany-koncern-accordion-header"
                        onClick={() =>
                          setOpenValuationId((prev) => (prev === item.id ? null : item.id))
                        }
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: 14 }}>{item.title}</strong>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {item.subtitle !== item.title && <span>{item.subtitle} · </span>}
                            {item.orgnr}
                          </div>
                        </div>
                        <span style={{ fontSize: 13, color: "#374151", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {item.valuation.loading
                            ? "Laddar…"
                            : formatRange(item.valuation.equityLow, item.valuation.equityHigh)}
                        </span>
                        <span style={{ color: "#9ca3af", fontSize: 18, flexShrink: 0 }}>
                          {isOpen ? "−" : "+"}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="checkcompany-koncern-accordion-body">
                          <div className="checkcompany-val-main" style={{ marginTop: 8 }}>
                            <span className="checkcompany-val-label">Equity value</span>
                            <p className="checkcompany-val-range" style={{ fontSize: 20 }}>
                              {formatRange(item.valuation.equityLow, item.valuation.equityHigh)}
                            </p>
                          </div>
                          <p className="checkcompany-val-meta">
                            {item.valuation.asOfDate
                              ? `Baserat på bokslut per ${new Date(item.valuation.asOfDate).toLocaleDateString("sv-SE")}`
                              : "Baserat på senaste tillgängliga bokslut"}
                            {item.valuation.metricLabel
                              ? ` · nyckeltal: ${item.valuation.metricLabel.toLowerCase()}`
                              : ""}
                          </p>
                          <div className="checkcompany-val-divider" />
                          <div className="checkcompany-ev-header">
                            <span className="checkcompany-ev-label">Enterprise Value (EV)</span>
                            <span className="checkcompany-ev-range">
                              {formatRange(item.valuation.evLow, item.valuation.evHigh)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="checkcompany-choice-panel">
            {minimal ? (
              <>
                <p className="checkcompany-choice-sublead">
                  Du kommer behöva svara på några grundläggande frågor inför en riktig genomlysning
                </p>

                <div className="checkcompany-choice-options">
                  <Link
                    to={`/dashboard/${cleanOrgnr}`}
                    className="checkcompany-choice-btn checkcompany-choice-btn--primary"
                  >
                    Nästa
                  </Link>
                </div>
              </>
            ) : (
              <>
                <p className="checkcompany-choice-lead">Vad vill du göra med den här värderingen?</p>
                <p className="checkcompany-choice-sublead">
                  Du kommer ändå behöva svara på några grundläggande frågor inför en riktig genomlysning – här kan du välja om du vill ta dem nu eller börja i plattformen.
                </p>

                <div className="checkcompany-choice-options">
                  <Link
                    to={`/dashboard/${cleanOrgnr}`}
                    className="checkcompany-choice-btn checkcompany-choice-btn--primary"
                  >
                    Gå in i plattformen
                  </Link>

                  <button
                    type="button"
                    className="checkcompany-choice-btn checkcompany-choice-btn--secondary"
                    onClick={() => setStep("questionnaire")}
                  >
                    Svara på försäljningsfrågor
                  </button>

                  <button
                    type="button"
                    className="checkcompany-choice-btn checkcompany-choice-btn--ghost"
                    onClick={handleContinue}
                  >
                    Hoppa över frågor – gå till plattformen
                  </button>
                </div>

                <p className="checkcompany-choice-help">
                  Du kan göra delarna i valfri ordning. När ett formulär är ifyllt och sparat betraktas den delen som klar.
                </p>

                <button
                  type="button"
                  className="checkcompany-linkbtn checkcompany-choice-back"
                  onClick={() => setStep("check")}
                >
                  ← Justera bokslut
                </button>

                <p className="checkcompany-choice-tagline">
                  När vi har både bokslut och frågeunderlag kan vi ta fram en beslutsunderbyggd värdering – inte bara ett tal.
                </p>
              </>
            )}
          </section>
        </div>
      </div>
    );
  };

  const renderQuestionnaireStep = () => (
    <div className="questionnaire-wrap">
      <div className="questionnaire-card">
        <div className="questionnaire-header">
          <h1 className="questionnaire-title">Försäljningsfrågor</h1>
          <p className="questionnaire-subtitle">
            Hjälp oss få en fullständig bild inför ett investeringsmemorandum. Du kan hoppa över frågor som inte är relevanta.
          </p>
        </div>

        <OwnerQuestionnaireForm orgnr={orgnr} />

        <div className="questionnaire-footer">
          <button type="button" className="questionnaire-backbtn" onClick={() => setStep("choice")}>
            ← Tillbaka
          </button>
        </div>
      </div>
    </div>
  );

  const renderRiskStep = () => (
    <div className="questionnaire-wrap">
      <div className="questionnaire-card">
        <div className="questionnaire-header">
          <h1 className="questionnaire-title">Riskprofil</h1>
          <p className="questionnaire-subtitle">
            Här skattar vi några centrala riskfaktorer som påverkar värderingsmultipeln. Utgå från hur bolaget ser ut idag.
          </p>
        </div>

        <RiskProfileQuestionnaireForm orgnr={orgnr} />

        <div className="questionnaire-footer">
          <button type="button" className="questionnaire-backbtn" onClick={() => setStep("choice")}>
            ← Tillbaka
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Main navItems={makeCompanyNav(orgnr)}>
      <div className="checkcompany-wrap">
        {step === "check" && renderCheckStep()}
        {step === "choice" && renderOverview(false)}
        {step === "questionnaire" && renderQuestionnaireStep()}
        {step === "risk" && renderRiskStep()}
        {step === "minimal_choice" && renderOverview(true)}

        {processing && (
          <div className="checkcompany-overlay">
            <div className="checkcompany-loader-box">
              <div className="checkcompany-spinner" />
              <div className="checkcompany-loader-text">{processMsg}</div>
            </div>
          </div>
        )}
      </div>
    </Main>
  );
}